import type { ActionFunctionArgs, LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import { redirect } from '@remix-run/node';
import { Form, Link, useActionData, useLoaderData } from '@remix-run/react';
import { Paperclip } from 'lucide-react';
import { z } from 'zod';
import { FactsStrip, TextField } from '~/components/way-in/brand-field';
import { MarginRail } from '~/components/way-in/margin-rail';
import { WayInButton, WayInLink } from '~/components/way-in/way-in-button';
import { ActionAside, WayInShell } from '~/components/way-in/way-in-shell';
import { Eyebrow, FineNote, GroupLabel, Headline, Lede } from '~/components/way-in/way-in-type';
import { formatDate } from '~/data/dates';
import { INVITE } from '~/data/way-in-seed';
import { commitWayInAnswers, getWayInAnswers } from '~/way-in-session.server';

export const meta: MetaFunction = () => [{ title: 'Who you are | Science Exchange' }];

const contactSchema = z.object({
  email: z.string().trim().email('Enter the work email the sign-in link should go to.'),
  name: z.string().trim().min(1, 'Tell us your name.'),
});

export const loader = async ({ request }: LoaderFunctionArgs) => {
  // The buyer-invite URL carries ?case= and ?token= (PRD §2.3). Until the case lookup
  // exists, the seeded invite plays the case; the parameters are accepted and unused.
  const answers = await getWayInAnswers(request);
  return {
    invite: INVITE,
    contact: answers.contact ?? INVITE.supplierContact,
  };
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const parsed = contactSchema.safeParse({
    email: formData.get('email'),
    name: formData.get('name'),
  });
  if (!parsed.success) {
    return { errors: parsed.error.flatten().fieldErrors };
  }
  const headers = await commitWayInAnswers(request, { contact: parsed.data });
  return redirect('/onboard/organization', { headers });
};

export default function WhoYouAre() {
  const { invite, contact } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const errors = actionData?.errors;

  return (
    <Form method="post">
      <WayInShell
        pill="top-right"
        rail={
          <MarginRail
            mark={{ kind: 'numeral', value: '01' }}
            label="Who you are"
            progress={25}
            position="Step 1 of 4"
            note="Everything about your business is the next screen."
          />
        }
        actions={
          <>
            <WayInButton>Start</WayInButton>
            <WayInLink to="/onboard/pass-on">Pass this on</WayInLink>
            <ActionAside>About 2 minutes, all four steps</ActionAside>
          </>
        }
      >
        <Eyebrow className="mb-[18px]">An invitation from {invite.sponsor}</Eyebrow>
        <Headline className="max-w-[17ch]">
          {invite.sponsorShort} asked us to set you up as a supplier.
        </Headline>
        <Lede>
          {invite.contact} raised this on {invite.raisedOn} and named you. Both fields below came
          from their request.
        </Lede>

        <FactsStrip
          facts={[
            { key: 'Who asked', value: invite.sponsor },
            { key: 'Their contact', value: invite.contact },
            ...(invite.neededBy ? [{ key: 'Needed by', value: formatDate(invite.neededBy) }] : []),
          ]}
        />

        {invite.notes ? (
          <figure className="m-0 mt-5 border-l-2 border-brand-secondary pl-[18px]">
            <GroupLabel className="mb-1.5">Their note to you</GroupLabel>
            <blockquote className="m-0 max-w-[60ch] font-display text-brand-fact text-brand-black">
              &ldquo;{invite.notes}&rdquo;
            </blockquote>
            {invite.attachment ? (
              <Link
                to={invite.attachment.href}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex max-w-full items-center gap-3 rounded-button border border-brand-line bg-brand-ground px-3.5 py-2.5 no-underline hover:border-brand-line-strong"
              >
                <Paperclip className="size-4 shrink-0 text-brand-muted" strokeWidth={1.5} />
                <span className="min-w-0">
                  <span className="block truncate text-brand-meta-strong text-brand-ink">
                    {invite.attachment.name}
                  </span>
                  <span className="block truncate text-brand-meta text-brand-muted">
                    {invite.attachment.detail}
                  </span>
                </span>
              </Link>
            ) : null}
          </figure>
        ) : null}

        <div className="mt-7 grid grid-cols-1 gap-[22px] md:grid-cols-2">
          <TextField
            label="Work email"
            name="email"
            type="email"
            autoComplete="email"
            defaultValue={contact.email}
            hint={errors?.email?.[0]}
            required
          />
          <TextField
            label="Your name"
            name="name"
            type="text"
            autoComplete="name"
            defaultValue={contact.name}
            hint={errors?.name?.[0]}
            required
          />
        </div>
        <FineNote className="mt-3.5">
          A sign-in link goes to that address. No password today. {invite.contact} sees your
          progress, never your answers, documents or bank details.
        </FineNote>
      </WayInShell>
    </Form>
  );
}
